import { useCallback, useEffect, useState } from 'react';
import {
  CalendarOutlined,
  EditOutlined,
  FileTextOutlined,
  FormOutlined,
  IdcardOutlined,
  ProfileOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { AModal, AInput, ASelect } from '../../../components';
import {
  IGuide,
  IClient,
  IGuideStatus,
  ICovenant,
  IProfessional,
} from '../../../shared/interfaces';
import { Card, Col, Form, Row, Space, Typography } from 'antd';
import { schemaGuide } from '../schemas/yup.guide.schema';
import { useToast } from '../../../hooks/toast';
import TextArea from 'antd/es/input/TextArea';
import { patchGuide, postGuide } from '../../../http';
import {
  formatToBRL,
  parseCurrencyInput,
} from '../../../shared/utils/functions';

const { Text } = Typography;

interface IGuideProps {
  open: boolean;
  close: () => void;
  guide: IGuide | null;
  setGuides: React.Dispatch<React.SetStateAction<IGuide[]>>;
  clients: IClient[];
  professionals: IProfessional[];
  guidesStatus: IGuideStatus[];
  covenants: ICovenant[];
}

const GuideCreateEdit: React.FC<IGuideProps> = ({
  open,
  close,
  guide,
  setGuides,
  clients,
  professionals,
  guidesStatus,
  covenants,
}) => {
  const { addToast } = useToast();
  const isEditing = !!guide;
  const [guideNumber, setGuideNumber] = useState<string>();
  const [authorizationDate, setAuthorizationDate] = useState<string>();
  const [professionalId, setProfessionalId] = useState<string>();
  const [clientId, setClientId] = useState<string>();
  const [covenantId, setCovenantId] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [value, setValue] = useState<number>(0);
  const [status, setStatus] = useState<string>();
  const [observation, setObservation] = useState<string>();

  const resetForm = useCallback(() => {
    setGuideNumber('');
    setAuthorizationDate('');
    setProfessionalId(undefined);
    setClientId(undefined);
    setCovenantId(undefined);
    setDescription('');
    setValue(0);
    setStatus(undefined);
    setObservation('');
  }, []);

  const handleCreate = useCallback(() => {
    schemaGuide
      .validate({
        client_id: clientId,
        professional_id: professionalId,
        covenant_id: covenantId,
        description,
        value,
        status,
        observation,
        guide_number: guideNumber,
        autorization_date: authorizationDate ?? null,
      })
      .then(async (validated) => {
        await postGuide(validated)
          .then((data) => {
            setGuides((state) => [...state, data]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Cadastrado com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [
    clientId,
    professionalId,
    covenantId,
    description,
    status,
    value,
    observation,
    guideNumber,
    authorizationDate,
    setGuides,
    addToast,
    close,
  ]);

  const handleEdit = useCallback(() => {
    schemaGuide
      .validate({
        client_id: clientId,
        professional_id: professionalId,
        covenant_id: covenantId,
        description,
        value,
        status,
        observation,
        guide_number: guideNumber,
        authorization_date: authorizationDate !== '' ? authorizationDate : null,
      })
      .then(async (validated) => {
        await patchGuide(guide?.id, validated)
          .then((data) => {
            setGuides((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: 'Alterado',
              description: 'Dados alterados com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao editar.',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [
    clientId,
    professionalId,
    covenantId,
    description,
    value,
    status,
    observation,
    guideNumber,
    authorizationDate,
    guide?.id,
    setGuides,
    addToast,
    close,
  ]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setValue(numericValue);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (guide) {
      setCovenantId(guide.covenant_id);
      setDescription(guide.description);
      setClientId(guide.client_id);
      setProfessionalId(guide.professional_id);
      setValue(guide.value);
      setStatus(guide.status);
      setGuideNumber(guide.guide_number);
      setAuthorizationDate(guide.authorization_date?.split('T')[0]);
      setObservation((guide as any).observation ?? '');
      return;
    }

    resetForm();
  }, [open, guide, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> Editar guia Nº {guide.code}
          </>
        ) : (
          <>
            <FormOutlined /> Cadastrar
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={800}
    >
      <Form layout="vertical" style={{ marginTop: 8 }}>
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <IdcardOutlined style={{ color: '#2f54eb' }} />
                    <span>Número da guia</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="Informe o número da guia"
                  name="guideNumber"
                  value={guideNumber}
                  onChange={(e) => setGuideNumber(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <CalendarOutlined style={{ color: '#13c2c2' }} />
                    <span>Data de autorização</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="Informe a data"
                  name="autorizationDate"
                  type="date"
                  value={authorizationDate}
                  onChange={(e) => setAuthorizationDate(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          size="small"
          title="Dados da guia"
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Preencha os campos obrigatórios para vincular o atendimento
            </Text>
          }
          style={{ marginBottom: 12 }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Convênio" required>
                <ASelect
                  showSearch
                  placeholder="Selecione o convênio"
                  value={covenantId}
                  onChange={(e) => setCovenantId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  options={covenants.map((cov) => ({
                    label: cov.description,
                    value: cov.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Profissional" required>
                <ASelect
                  showSearch
                  placeholder="Selecione o profissional"
                  value={professionalId}
                  onChange={(e) => setProfessionalId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  options={professionals.map((prof) => ({
                    label: prof.name,
                    value: prof.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Cliente" required>
                <ASelect
                  showSearch
                  placeholder="Selecione o cliente"
                  value={clientId}
                  onChange={(e) => setClientId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  options={clients.map((client) => ({
                    label: client.corporate_name,
                    value: client.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <FileTextOutlined style={{ color: '#1677ff' }} />
                    <span>Descrição</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Informe a descrição da guia"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Valor" required>
                <AInput
                  placeholder="Informe o valor"
                  name="value"
                  value={formatToBRL(value)}
                  onChange={handlePriceChange}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <SolutionOutlined style={{ color: '#fa8c16' }} />
                    <span>Status</span>
                  </Space>
                }
                required
              >
                <ASelect
                  placeholder="Selecione o status"
                  value={status}
                  onChange={(e) => setStatus(e)}
                  allowClear
                  options={guidesStatus.map((item) => ({
                    label: item.description,
                    value: item.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ProfileOutlined style={{ color: '#595959' }} />
                    <span>Observações</span>
                  </Space>
                }
              >
                <TextArea
                  rows={4}
                  placeholder="Informações adicionais da guia (opcional)"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {value > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Valor informado: {formatToBRL(value)}
          </Text>
        )}
      </Form>
    </AModal>
  );
};

export default GuideCreateEdit;
